import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { get as _get } from 'lodash';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { HttpOptions } from '../+state/geo-coder/geo-coder.models';
import {
  DEFAULT_NAME_DISTANCE,
  DrpSearch,
  SEARCH_TYPES,
  SearchContext,
  SearchedValue,
  SearchResult
} from '../+state/service-locator/service-locator.models';
import { DrpLocatorAdapter } from './../services/drp-locator-adapter';
import { RentalLocatorAdapter } from './../services/rental-locator-adapter';

@Injectable({
  providedIn: 'root'
})
export class DrpLocatorService {
  constructor(
    private http: HttpClient,
    private drpLocatorAdapter: DrpLocatorAdapter,
    private rentalLocatorAdapter: RentalLocatorAdapter
  ) {}

  getShops(
    payload: SearchedValue,
    context: SearchContext,
    url: string
  ): Observable<SearchResult[]> {
    if (context === SearchContext.RENTAL_SELECTION) {
      return this.getRentalShops(payload, url);
    } else {
      const typedPayload = new DrpSearch(payload);
      const type = typedPayload.getSearchType();
      let params: HttpParams;
      // Determine what search payload is given -- TODO: may be move this to the class
      switch (type) {
        case SEARCH_TYPES.LOCATION:
          params = this.getLocationParams(payload);
          break;
        case SEARCH_TYPES.NAME:
          params = this.getNameParams(payload);
          break;
        case SEARCH_TYPES.COORDINATES:
          params = this.getCoordinateParams(payload);
          break;
      }
      let options: HttpOptions = {
        params: params
      };

      if (context === SearchContext.DRP_SHOP_SEARCH) {
        options = { ...options, headers: this.getHeaders() };
        return this.getRepairShops(typedPayload, url, options);
      }
      return this.getRepairShops(typedPayload, url, options);
    }
  }

  private getRepairShops(
    typedPayload: DrpSearch,
    url: string,
    options: HttpOptions
  ): Observable<SearchResult[]> {
    return this.http.get<SearchResult[]>(url + 'shops', options).pipe(
      map((response: any) => {
        if (_get(response, 'status.code') === 200) {
          return this.drpLocatorAdapter.adapt(_get(typedPayload.sort(response), 'shops'));
        } else {
          throw new Error();
        }
      })
    );
  }
  /** Given one of the following:
   * street, city, state, zip, distance
   * zip and distance
   * shop name and zip
   * shop name, city, state
   * lat/long coordinates and distance
   *
   * retrieve a list of DRP shops in that given area
   * @param payload address user inputed
   * @return list of search results
   */
  private getRentalShops(payload: SearchedValue, url: string): Observable<any[]> {
    const typedPayload = new DrpSearch(payload);
    const type = typedPayload.getSearchType();
    let params;
    // Determine what search payload is given -- TODO: may be move this to the class
    switch (type) {
      case SEARCH_TYPES.LOCATION:
      case SEARCH_TYPES.NAME:
        params = this.getRentalParams(payload);
        break;
      case SEARCH_TYPES.COORDINATES:
        params = this.getCoordinateParams(payload);
        break;
    }

    const options = {
      params: params
    };

    return this.http.get<SearchResult[]>(url + 'rentallocations', options).pipe(
      map((response: any) => {
        if (_get(response, 'status.code') === 200) {
          return this.rentalLocatorAdapter.adapt(_get(response, 'branchDetails'));
        } else {
          throw new Error();
        }
      })
    );
  }

  /**
   * Set HTTP request parameters for address location search
   * @param  res full address + distance or just zip + distance
   */
  private getLocationParams(location: SearchedValue): HttpParams {
    return new HttpParams()
      .set('addressLine1', location.line1 ? location.line1 : '')
      .set('city', location.city ? location.city : '')
      .set('state', location.state ? location.state : '')
      .set('zip', location.zip)
      .set('distance', location.distance);
  }

  /**
   * Set HTTP request parameters for shop name search
   * @param name shop name + zip or shop name + city + state
   */
  private getNameParams(name: SearchedValue): HttpParams {
    return new HttpParams()
      .set('shopName', name.shopName)
      .set('city', name.city ? name.city : '')
      .set('state', name.state ? name.state : '')
      .set('zip', name.zip ? name.zip : '')
      .set('distance', DEFAULT_NAME_DISTANCE);
  }

  /**
   * Set HTTP request parameters for coordinate search
   * @param coords lat + lng + distance
   */
  private getCoordinateParams(coords: SearchedValue): HttpParams {
    return new HttpParams()
      .set('latitude', coords.lat.toString()) // TODO: think about where to actually set this as a string
      .set('longitude', coords.lng.toString())
      .set('distance', coords.distance);
  }

  /**
   * Set HTTP request parameters for rental search
   * @param coords lat + lng + distance
   */
  private getRentalParams(searchedValue: SearchedValue): HttpParams {
    return new HttpParams().set('zip', searchedValue.zip);
  }

  private getRentalHeaders(): HttpHeaders {
    return new HttpHeaders({
      'afi-api-key': 'sai78y23kjrewfdsiur2jn3we'
    });
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json'
    });
  }
}

// https://devssg.amfam.com/abssmartlocator/v1/shops?zip=53911&distance=5';
